import { Field, FieldProps, Form, Formik, useFormikContext } from "formik";
import * as Yup from "yup";
import { TextField } from "@mui/material";
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDButton from "components/MDButton";
import MenuItem from "@mui/material/MenuItem";
import colors from "assets/theme-dark/base/colors";
import { CRUXY_ADMIN, CRUXY_USER } from "common/names";
import { ClientsResponse, User } from "graphql/types/graphql";

interface FormValue {
  cruxyId: string;
  role: string;
  email: string;
  password: string;
  repeatPassword: string;
}

interface NewUserFormProps {
  user: User;
  companies: ClientsResponse[];
  roles: string[];
  errorEmail: boolean;
  errorRepeatPass: boolean;
  errorPass: boolean;
  errorUser: string;
  setErrorEmail: (error: boolean) => void;
  setErrorRepeatPass: (error: boolean) => void;
  setErrorPass: (error: boolean) => void;
  setErrorUser: (error: string) => void;
  signUpHandler: (values: FormValue) => Promise<void>;
}

const LoginSchema = Yup.object().shape({
  cruxyId: Yup.string().required("CruxyId required!"),
  role: Yup.string().required("Role required!"),
  email: Yup.string().email("Invalid email!").required("Email required!"),
  password: Yup.string().required("Password required!"),
  repeatPassword: Yup.string()
    .required("Confirm password required!")
    .test("", "Password fields must match", function (value) {
      return this.parent.password === value;
    }),
});

function AddUserButton() {
  const { values, errors } = useFormikContext<FormValue>();
  const isDisabled =
    !values.cruxyId ||
    !values.role ||
    !values.email ||
    !values.password ||
    !values.repeatPassword ||
    Object.keys(errors).length > 0;
  return (
    <MDButton
      type="submit"
      variant="background"
      size="large"
      fullWidth
      disabled={isDisabled}
      sx={{ borderRadius: "4px" }}
    >
      Add User
    </MDButton>
  );
}

function ErrorTypography(props: { errorEmail: boolean; erroPass: boolean; errorRepeatPass: boolean }): JSX.Element {
  const { errorEmail, erroPass, errorRepeatPass } = props;
  const context = useFormikContext<FormValue>();
  const item = context.errors;

  if (Object.keys(item).length === 0) {
    return (
      <MDTypography mt={2} color="error">
        {" "}
      </MDTypography>
    );
  }
  if (item?.email && errorEmail) {
    return (
      <MDTypography mt={2} color="error">
        {item.email}
      </MDTypography>
    );
  }
  if (item?.password && erroPass) {
    return (
      <MDTypography mt={2} color="error">
        {item.password}
      </MDTypography>
    );
  }
  if (item?.repeatPassword && errorRepeatPass) {
    return (
      <MDTypography mt={2} color="error">
        {item.repeatPassword}
      </MDTypography>
    );
  }
  return (
    <MDTypography mt={2} color="error">
      {" "}
    </MDTypography>
  );
}

function NewUserForm({
  user,
  companies,
  roles,
  errorEmail,
  errorRepeatPass,
  errorPass,
  errorUser,
  setErrorEmail,
  setErrorRepeatPass,
  setErrorPass,
  setErrorUser,
  signUpHandler,
}: NewUserFormProps): JSX.Element {
  return (
    <>
      <MDTypography
        sx={{
          color: colors.text.main,
          fontSize: "1.5rem",
          fontWeight: 600,
          marginBottom: "2rem",
        }}
      >
        New User
      </MDTypography>
      <Formik
        initialValues={{
          email: "",
          password: "",
          repeatPassword: "",
          cruxyId: user.cruxyId,
          role: "",
        }}
        validationSchema={LoginSchema}
        onSubmit={async (values) => {
          await signUpHandler(values);
        }}
      >
        <Form style={{ width: "100%" }}>
          {Boolean(user.role === CRUXY_ADMIN || user.role === CRUXY_USER) && (
            <Field sx={{ width: "100%" }} name="cruxyId">
              {(props: FieldProps<string, FormValue>) => (
                <div style={{ display: "flex", alignItems: "center" }}>
                  <TextField
                    sx={{
                      width: "100%",
                      marginBottom: "1.5rem",
                      "& .MuiSelect-select": {
                        color: colors.primary.main,
                      },
                    }}
                    size="small"
                    name="cruxyId"
                    select
                    SelectProps={{
                      MenuProps: {
                        anchorOrigin: {
                          vertical: "bottom",
                          horizontal: "center",
                        },
                        PaperProps: {
                          style: {
                            maxHeight: "200px",
                          },
                        },
                      },
                    }}
                    label={"Cruxy ID"}
                    defaultValue="Cruxy ID"
                    required
                    value={props.field.value}
                    onChange={(e) => {
                      props.form.setFieldValue(props.field.name, e.target.value, true);
                    }}
                  >
                    {companies?.map((option: ClientsResponse) => (
                      <MenuItem key={option.cruxyId} value={option?.cruxyId}>
                        {option?.name}
                      </MenuItem>
                    ))}
                  </TextField>
                </div>
              )}
            </Field>
          )}
          <Field sx={{ width: "100%" }} name="role">
            {(props: FieldProps<string, FormValue>) => (
              <div style={{ display: "flex", alignItems: "center" }}>
                <TextField
                  size="small"
                  sx={{
                    width: "100%",
                    marginBottom: "1.5rem",
                    "& .MuiSelect-select": {
                      color: colors.primary.main,
                    },
                  }}
                  name="role"
                  select
                  SelectProps={{
                    MenuProps: {
                      anchorOrigin: {
                        vertical: "bottom",
                        horizontal: "center",
                      },
                      PaperProps: {
                        style: {
                          maxHeight: "200px",
                        },
                      },
                    },
                    style: { color: "red" },
                  }}
                  label={"Role"}
                  defaultValue="Role"
                  required
                  value={props.field.value}
                  onChange={(e) => {
                    props.form.setFieldValue(props.field.name, e.target.value, true);
                  }}
                >
                  {roles?.map((option: string) => (
                    <MenuItem key={option} value={option}>
                      {option}
                    </MenuItem>
                  ))}
                </TextField>
              </div>
            )}
          </Field>
          <Field sx={{ width: "100%" }} name="email">
            {(props: FieldProps<string, FormValue>) => (
              <TextField
                size="small"
                onBlur={() => setErrorEmail(true)}
                inputProps={{ "data-testid": "emailForm", style: { color: colors.primary.main } }}
                sx={{ width: "100%", marginBottom: "1.5rem" }}
                name="Email"
                label={"Email"}
                color="secondary"
                autoFocus
                required
                value={props.field.value}
                onChange={(e) => {
                  setErrorUser(" ");
                  setErrorEmail(false);
                  props.form.setFieldValue(props.field.name, e.target.value, true);
                }}
              />
            )}
          </Field>
          <Field name="password">
            {(props: FieldProps<string, FormValue>) => (
              <TextField
                size="small"
                onBlur={() => setErrorPass(true)}
                inputProps={{
                  "data-testid": "passwordForm",
                  style: { color: colors.primary.main },
                }}
                sx={{ width: "100%", marginBottom: "1.5rem" }}
                color="secondary"
                name="Password"
                label={"Password"}
                type="password"
                required
                value={props.field.value}
                onChange={(e) => {
                  setErrorUser(" ");
                  setErrorPass(false);
                  props.form.setFieldValue(props.field.name, e.target.value, true);
                }}
              />
            )}
          </Field>
          <Field name="repeatPassword">
            {(props: FieldProps<string, FormValue>) => (
              <TextField
                size="small"
                onBlur={() => setErrorRepeatPass(true)}
                inputProps={{
                  "data-testid": "passwordForm",
                  style: { color: colors.primary.main },
                }}
                sx={{ width: "100%" }}
                color="secondary"
                name="repeatPassword"
                label={"Confirm Password"}
                type="password"
                required
                value={props.field.value}
                onChange={(e) => {
                  setErrorUser(" ");
                  setErrorRepeatPass(false);
                  props.form.setFieldValue(props.field.name, e.target.value, true);
                }}
              />
            )}
          </Field>

          <MDBox mt={2} sx={{ minHeight: "2rem" }}>
            <ErrorTypography errorEmail={errorEmail} erroPass={errorPass} errorRepeatPass={errorRepeatPass} />
          </MDBox>

          <MDBox mb={1}>
            <AddUserButton />
          </MDBox>
        </Form>
      </Formik>
    </>
  );
}

export default NewUserForm;
